import React, { Fragment, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
} from "reactstrap";
import { Clock, Power, RefreshCcw } from "react-feather";
import { deleteDeviceSchedule, getDevices, switchDevices } from "../../api/device.api";
import { Breadcrumbs } from "../../AbstractElements";
import { titleCase, sentenceCase } from "../../utils/case";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatIsoToDatetime } from "../../utils/datetime";

const DeviceMain = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("1");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switchingIds, setSwitchingIds] = useState([]);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const res = await getDevices();
      setDevices(res.data || []);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  // ✅ Optimistic toggle with revert + per-device loading
  const handleToggle = async (deviceId, checked) => {
    const confirmed = window.confirm(
      checked
        ? titleCase(t("confirm turn on"))
        : titleCase(t("confirm turn off"))
    );

    if (!confirmed) return;

    // add to loading list
    setSwitchingIds((prev) => [...prev, deviceId]);

    // optimistic update
    setDevices((prev) =>
      prev.map((d) =>
        d.id === deviceId
          ? { ...d, status: checked ? "on" : "off" }
          : d
      )
    );

    try {
      const res = await switchDevices({
        ids: [deviceId],
        state: checked,
      });

      const isSuccess =
        res?.results?.every((r) => r.success === true) ||
        res?.success === true;

      if (!isSuccess) {
        // revert on failure
        setDevices((prev) =>
          prev.map((d) =>
            d.id === deviceId
              ? { ...d, status: checked ? "off" : "on" }
              : d
          )
        );
      }
    } catch (err) {
      console.error(err.message);

      // revert on error
      setDevices((prev) =>
        prev.map((d) =>
          d.id === deviceId
            ? { ...d, status: checked ? "off" : "on" }
            : d
        )
      );
    } finally {
      // remove from loading list
      setSwitchingIds((prev) =>
        prev.filter((id) => id !== deviceId)
      );
    }
  };

  // ✅ Bulk switch with confirmation
  const handleBulkSwitch = async (state) => {
    const confirmed = window.confirm(
      state
        ? titleCase(t("confirm turn all on"))
        : titleCase(t("confirm turn all off"))
    );

    if (!confirmed) return;

    const ids = devices.map((d) => d.id);

    // optimistic update
    setDevices((prev) =>
      prev.map((d) => ({
        ...d,
        status: state ? "on" : "off",
      }))
    );

    try {
      const res = await switchDevices({ ids, state });

      const isSuccess =
        res?.results?.every((r) => r.success === true) ||
        res?.success === true;

      if (!isSuccess) {
        console.error("Bulk switch failed", res);
        fetchDevices(); // fallback to truth
      } else {
        fetchDevices();
      }
    } catch (err) {
      console.error(err.message);
      fetchDevices(); // revert via refetch
    }
  };

  const handleViewSchedules = (device) => {
    setSelectedDevice(device);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    const confirmed = window.confirm(
      titleCase(t("are you sure you want to delete this schedule?"))
    );

    if (!confirmed) return;

    try {
      await deleteDeviceSchedule(scheduleId);

      // ✅ Update modal state (remove deleted schedule)
      setSelectedDevice((prev) => ({
        ...prev,
        schedules: prev.schedules.filter((s) => s.id !== scheduleId),
      }));
    } catch (err) {
      console.error(err.message);
    }
  };

  const renderDeviceCard = (device) => (
    <Col className="col-xxl-4" md="6" key={device.id}>
      <Card className="project-box">
        <CardBody>
          <h6>{device.name}</h6>

          <p className="text-muted mb-3">
            {sentenceCase(t(device.type?.replace(/_/g, " ")))}
          </p>

          <p className="text-muted small mb-3">
            {sentenceCase(t("last synced"))}:{" "}
            {device.lastSyncedAt
              ? formatDateTime(device.lastSyncedAt)
              : sentenceCase(t("never"))}
          </p>

          <div className="d-flex align-items-center">
            <Label className="switch m-r-10 mb-0">
              <Input
                type="checkbox"
                checked={device.status === "on"}
                disabled={
                  loading || switchingIds.includes(device.id)
                }
                onChange={(e) =>
                  handleToggle(device.id, e.target.checked)
                }
              />
              <span className="switch-state"></span>
            </Label>
          </div>

          <div className="d-flex justify-content-end gap-3 mt-3 align-items-center">
            <Clock
              size={18}
              style={{ cursor: "pointer", color: "blue" }}
              title="View"
              onClick={() => handleViewSchedules(device)}
            />
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("device"))}
        title={titleCase(t("devices management"))}
        mainTitle={titleCase(t("devices management"))}
      />

      <Container fluid>
        <Row className="project-card">
          <Col className="project-list">
            <Card>
              <Row>
                <Col md="6">
                  <Nav tabs className="border-tab">
                    <NavItem>
                      <NavLink
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        {titleCase(t("all"))}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => setActiveTab("2")}
                      >
                        {titleCase(t("on"))}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => setActiveTab("3")}
                      >
                        {titleCase(t("off"))}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </Col>

                <Col md="6" className="text-end">
                  <Button
                    color="success"
                    className="me-2"
                    onClick={() => handleBulkSwitch(true)}
                  >
                    <Power size={16} /> {titleCase(t("turn all on"))}
                  </Button>
                  <Button
                    color="danger"
                    onClick={() => handleBulkSwitch(false)}
                  >
                    <Power size={16} /> {titleCase(t("turn all off"))}
                  </Button>
                  <Button
                    color="info"
                    className="me-2"
                    onClick={() => fetchDevices()}
                  >
                    <RefreshCcw size={16} /> {titleCase(t("sync device"))}
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <CardBody>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <Row>
                      {devices.map((device) => renderDeviceCard(device))}
                    </Row>
                  </TabPane>

                  <TabPane tabId="2">
                    <Row>
                      {devices
                        .filter((d) => d.status === "on")
                        .map((device) => renderDeviceCard(device))}
                    </Row>
                  </TabPane>

                  <TabPane tabId="3">
                    <Row>
                      {devices
                        .filter((d) => d.status === "off")
                        .map((device) => renderDeviceCard(device))}
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal
          isOpen={scheduleModalOpen}
          toggle={() => setScheduleModalOpen(!scheduleModalOpen)}
          size="lg"
        >
          <ModalHeader toggle={() => setScheduleModalOpen(!scheduleModalOpen)}>
            {selectedDevice?.name} - {titleCase(t("scheduled operations"))}
          </ModalHeader>

          <ModalBody>
            {selectedDevice?.schedules?.length > 0 ? (
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>{titleCase(t("date & time"))}</th>
                    <th>{titleCase(t("action"))}</th>
                    <th>{titleCase(t("status"))}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDevice.schedules.map((s) => (
                    <tr key={s.id}>
                      <td>{formatIsoToDatetime(s.datetime)}</td>
                      <td>{t(s.action)}</td>
                      <td>
                        {s.status !== "completed" && s.status !== "skipped" ? (
                          <Button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteSchedule(s.id)}
                          >
                            {titleCase(t("delete"))}
                          </Button>
                        )
                        :
                        (
                          titleCase(t("completed"))
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted">
                {titleCase(t("no upcoming schedules"))}
              </p>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default DeviceMain;